import { Signal } from "@angular/core";
import { SchemaPath, max, min, validate } from "@angular/forms/signals";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Proto } from "../../pojo/Proto";

export namespace NotificationPolicyModel {
    export type ContactRole = "approver" | "fyi";

    export type ContactRef = {
        contactId: number;
        contactName: string;
    };

    export type Assignment = ContactRef & {
        role: ContactRole;
    };

    export type ApprovalType = { itemId?: number; itemName?: string };

    export type FormModel = {
        assignments: Assignment[];
        approvalType?: ApprovalType;
        days?: number;
        hours?: number;
        minutes?: number;
        removedPolicy?: boolean;
    };

    /** Normalized shape for signal-form binding (`draftForForm` always supplies every field). */
    export type FormBindingModel = {
        assignments: Assignment[];
        approvalType: ApprovalType;
        days: number;
        hours: number;
        minutes: number;
        removedPolicy: boolean;
    };

    /** Approval window bounds from the existing numeric inputs. */
    export const APPROVAL_WINDOW_DAYS_MAX = 65536;
    export const APPROVAL_WINDOW_HOURS_MAX = 23;
    export const APPROVAL_WINDOW_MINUTES_MAX = 59;

    export function defaultLayout(): LayoutConfig {
        return { showApprovalWindow: true, showQuorum: true };
    }

    export namespace Validators {
        export function days(path: SchemaPath<number>) {
            min(path, 0, { message: "Days must be 0 or more" });
            max(path, APPROVAL_WINDOW_DAYS_MAX, {
                message: `Days must be ${APPROVAL_WINDOW_DAYS_MAX} or fewer`,
            });
        }

        export function hours(path: SchemaPath<number>) {
            min(path, 0, { message: "Hours must be 0 or more" });
            max(path, APPROVAL_WINDOW_HOURS_MAX, { message: "Hours must be 23 or fewer" });
        }

        export function minutes(path: SchemaPath<number>) {
            min(path, 0, { message: "Minutes must be 0 or more" });
            max(path, APPROVAL_WINDOW_MINUTES_MAX, { message: "Minutes must be 59 or fewer" });
        }

        export function assignmentsRequired(path: SchemaPath<Assignment[]>, removedPolicy: SchemaPath<boolean>) {
            validate(path, ({ value, valueOf }) => {
                if (valueOf(removedPolicy)) {
                    return;
                }
                if (!value().length) {
                    return { kind: "required", message: "Please add a contact to this policy" };
                }
            });
        }

        export function quorum(
            approvalType: SchemaPath<ApprovalType>,
            assignments: SchemaPath<Assignment[]>,
            removedPolicy: SchemaPath<boolean>,
        ) {
            validate(approvalType, ({ value, valueOf }) => {
                if (valueOf(removedPolicy)) {
                    return;
                }
                const approverCount = valueOf(assignments).filter(
                    (assignment) => assignment.role === "approver",
                ).length;
                const itemId = value()?.itemId;
                if (approverCount >= 2 && itemId == null) {
                    return {
                        kind: "required",
                        message: "Please select how multiple approvers must approve",
                    };
                }
            });
        }
    }

    /** Imperative API hosts use instead of injecting a store. */
    export type Editor = {
        validate(): boolean;
        getSession(): Session;
        reset(): void;
        commitBaseline(): void;
        markPolicyRemoved(): void;
        beginPolicyEditing(): void;
        addContact(selected: PickerContact, role: ContactRole): void;
        removeContact(contactId: number): void;
        moveContact(contactId: number, role: ContactRole): void;
        setQuorum(approvalType: ApprovalType): void;
        setApprovalWindowField(field: "days" | "hours" | "minutes", rawValue: number | string): void;
        readonly pickerError: Signal<string>;
    };

    export type Session = {
        draft: FormModel;
        baseline: FormModel | null;
        isNewPolicy: boolean;
    };

    export type ObjectType = "requirement" | "location" | "resource" | "eventType" | "organization";

    /**
     * Notify-policy API object-type ids. These are not Item.Ids:
     * requirement is 8 here (evreq_notify); Item.Ids.Requirement is 7 (event type notify).
     */
    export const NotifyItemTypeId: Record<ObjectType, number> = {
        organization: 2,
        location: 4,
        resource: 6,
        eventType: 7,
        requirement: 8,
    };

    /** Notify-policy GET/PUT ids accepted by NotificationPolicyService — not Item.Ids (e.g. event type is 7 here, 19 on Item). */
    export type NotifyFetchItemTypeId = (typeof NotifyItemTypeId)[ObjectType];

    const objectTypeAliases: Record<string, ObjectType> = {
        space: "location",
    };

    export function resolveObjectType(raw: string | undefined): ObjectType | undefined {
        if (!raw) {
            return undefined;
        }
        const normalized = objectTypeAliases[raw] ?? raw;
        if (normalized in NotifyItemTypeId) {
            return normalized as ObjectType;
        }
        return undefined;
    }

    export function notifyItemTypeIdFromObjectType(type: ObjectType): NotifyFetchItemTypeId {
        return NotifyItemTypeId[type];
    }

    export function objectTypeFromNotifyItemTypeId(itemTypeId: number): ObjectType | undefined {
        for (const [objectType, notifyId] of Object.entries(NotifyItemTypeId) as [ObjectType, number][]) {
            if (notifyId === itemTypeId) {
                return objectType;
            }
        }
        return undefined;
    }

    export type LayoutConfig = {
        showApprovalWindow: boolean;
        showQuorum: boolean;
    };

    export type FetchKey = {
        itemTypeId: NotifyFetchItemTypeId;
        itemId: number;
        generation: number;
    };

    export type DraftSeed = {
        draft: FormModel;
        isNewPolicy: boolean;
    };

    /** Contact identity sent to the notify bulk API. */
    export type WireContactIdentity = {
        contact_id: number;
        notify_type: number;
    };

    export type WireContactRemove = {
        contact_id: number;
    };

    /** Wire row shape produced by the mapper at save time (maps to NotifyContact on PUT). */
    export type MapperWireContactRow = WireContactIdentity & {
        status: Proto.ServiceStatus;
    };

    /** PUT/POST body for *_notify.json (NotifyPoliciesDoc / NotifyPolicy). */
    export type NotifyPolicyPut = {
        status: Proto.ServiceStatus;
        object_id: string;
        approval_type: number;
        respond_within: string;
        contact: MapperWireContactRow[];
    };

    export type NotifyInfoBody = {
        notify_info: {
            notification_policy: NotifyPolicyPut;
        };
    };

    export type BulkPolicyObjectRef = {
        object_id: number;
    };

    /** PUT /notification/policy.json root document. */
    export type BulkPolicyPutRoot = {
        delete_all_contacts: 0 | 1;
        approval_type?: number;
        respond_within?: string;
        objects: BulkPolicyObjectRef[];
        contactsAdd: WireContactIdentity[];
        contactsRemove: WireContactRemove[];
    };

    /** DELETE /notification/policy.json root document. */
    export type BulkPolicyDeleteRoot = {
        approval_type: 0;
        objects: BulkPolicyObjectRef[];
    };

    export type BulkPolicyEnvelope<TRoot> = {
        root: TRoot;
    };

    /**
     * Contact row from GET notify policy after prettifyJson
     * (com.collegenet.series25.document.notifyPolicy.NotifyContact).
     */
    export type ApiGetContactRow = {
        contact_id?: string | number;
        contact_name?: string;
        notify_type?: string | number;
        contact_email?: string;
        contact_username?: string;
        notify_type_name?: string;
        status?: string;
    };

    /**
     * Policy payload from GET after prettifyJson
     * (com.collegenet.series25.document.notifyPolicy.NotifyPolicy).
     */
    export type ApiGetPolicy = {
        approval_type?: string | number;
        approval_type_name?: string;
        respond_within?: string;
        object_id?: string | number;
        object_name?: string;
        status?: string;
        contact?: ApiGetContactRow[];
    };

    /** GET notify policy (contact) or event-type form notifications (contacts, approval_from). */
    export type PolicyLikeSource = {
        approval_type?: string | number;
        approval_from?: string | number;
        respond_within?: string;
        contact?: ApiGetContactRow | ApiGetContactRow[];
        contacts?: ApiGetContactRow | ApiGetContactRow[];
    };

    /** DropDownItem-like shape for item-unwrapper and multiselect parents (not persisted as-is). */
    export type ContactDisplay = {
        itemId?: number;
        itemName?: string;
        notifyType?: { itemId?: number; itemName?: string };
        status?: Proto.ServiceStatus | string;
    };

    export type DraftValidationResult = {
        valid: boolean;
        message: string;
        draft: FormModel;
    };

    export type PickerContact = Pick<DropDownItem, "itemId" | "itemName">;
}
